// The four "Konfiguraation tila" cards, derived from the canonical completeness signal.
//
// Pure functions, kept apart from the fetching hook (useBuildingConfigStatus) so the mapping is
// testable directly — the same split the portfolio-scope setup lens uses with
// config-setup-lens.ts (both read TAB_CRITERIA / TAB_LABEL / TAB_ICON below, so a building's own
// tab bar and the portfolio situation cards can never disagree about which questions are open).
//
// Completeness is not computed here either: `GET /buildings/{id}/completeness` reports, per
// equipment item, which of the six criteria are `unmet`, and this module only decides which card
// owns which criterion (docs/spec/concepts.md § Configuration completeness).
//
// Ulkoiset palvelut is the one card outside the completeness model — the spec's criteria cover
// typing, variants, data points, config parameters, zone links and dependencies, not weather /
// price service activation — so that dimension reads the building's services instead.
import { Boxes, Sparkles, Radio, Plug, type LucideIcon } from "lucide-react";
import type { BuildingCompleteness, ItemCompleteness } from "@/lib/api/agent";
import type { BuildingService } from "@/lib/api/building";
import { formatRelativeFi } from "@/lib/building-services";
import {
  CRITERION_CONFIG,
  CRITERION_DATA_POINTS,
  CRITERION_DEPENDENCIES,
  CRITERION_TYPED,
  CRITERION_VARIANTS,
  CRITERION_ZONE,
  criterionLabel,
} from "@/lib/config-completeness";

export type ConfigTone = "ok" | "warn" | "empty";

// One card = one tab.
export type ConfigTabKey = "rakenne" | "kyvykkyydet" | "signaalit" | "palvelut";

export interface ConfigDimension {
  key: ConfigTabKey;
  label: string;
  value: string;
  hint: string;
  tone: ConfigTone;
}

export const TAB_LABEL: Record<ConfigTabKey, string> = {
  rakenne: "Rakenne",
  kyvykkyydet: "Kyvykkyydet & odotettu toiminta",
  signaalit: "Signaalit",
  palvelut: "Ulkoiset palvelut",
};

// Shared icon per question — the single-building tab bar (ConfigStatusTabs) and the portfolio
// overview's situation cards (OverviewSituationCards, via useConfigSetupLens) both render this,
// so the same question always carries the same icon.
export const TAB_ICON: Record<ConfigTabKey, LucideIcon> = {
  rakenne: Boxes,
  kyvykkyydet: Sparkles,
  signaalit: Radio,
  palvelut: Plug,
};

type CompletenessTabKey = Exclude<ConfigTabKey, "palvelut">;

// Which completeness criteria each tab is responsible for — the mapping that turns the canonical
// per-item `unmet` list into cards. Order within a tab drives hint display order.
export const TAB_CRITERIA: Record<CompletenessTabKey, string[]> = {
  rakenne: [CRITERION_TYPED, CRITERION_ZONE, CRITERION_DEPENDENCIES],
  kyvykkyydet: [CRITERION_VARIANTS, CRITERION_CONFIG],
  signaalit: [CRITERION_DATA_POINTS],
};

/** The items whose `unmet` list touches any of this tab's criteria. */
function itemsWithGap(
  items: ItemCompleteness[],
  criteria: string[],
): ItemCompleteness[] {
  return items.filter((item) => item.unmet.some((id) => criteria.includes(id)));
}

/** Names the gaps a tab can fix — its distinct unmet criteria, in the tab's display order. */
function gapHint(items: ItemCompleteness[], criteria: string[]): string {
  const unmet = new Set(items.flatMap((item) => item.unmet));
  return criteria
    .filter((id) => unmet.has(id))
    .map(criterionLabel)
    .join(" · ");
}

// The completeness endpoint did not answer. Say so instead of rendering a zero, which would read
// as a real "nothing is configured" answer.
function unavailableDimension(key: CompletenessTabKey): ConfigDimension {
  return {
    key,
    label: TAB_LABEL[key],
    value: "—",
    hint: "Valmiustietoa ei saatavilla",
    tone: "empty",
  };
}

function rakenneDimension(
  c: BuildingCompleteness,
  dependencyCount: number,
): ConfigDimension {
  const base = { key: "rakenne", label: TAB_LABEL.rakenne } as const;
  const value = `${c.total_items} · ${c.zone_count} · ${dependencyCount}`;
  if (c.total_items === 0 && c.zone_count === 0) {
    return {
      ...base,
      value,
      hint: "Aloita lisäämällä talotekniikkaa",
      tone: "empty",
    };
  }
  const gaps = itemsWithGap(c.items, TAB_CRITERIA.rakenne);
  if (gaps.length > 0) {
    return {
      ...base,
      value,
      hint: `${gaps.length} laitetta · ${gapHint(gaps, TAB_CRITERIA.rakenne)}`,
      tone: "warn",
    };
  }
  if (c.total_items === 0 || c.zone_count === 0) {
    return { ...base, value, hint: "Rakenne kesken", tone: "warn" };
  }
  // A zero dependency count is information, not a gap — completeness already reports
  // `unmet: ["dependencies"]` (handled above via TAB_CRITERIA) when the KB expects a link
  // that is missing, so an unmet-free zero here means none were ever expected.
  return {
    ...base,
    value,
    hint: "talotekniikkaa · vyöhykettä · riippuvuudet",
    tone: "ok",
  };
}

function kyvykkyydetDimension(c: BuildingCompleteness): ConfigDimension {
  const base = { key: "kyvykkyydet", label: TAB_LABEL.kyvykkyydet } as const;
  if (c.total_items === 0) {
    return {
      ...base,
      value: "—",
      hint: "Ei laitteita mallinnettavaksi",
      tone: "empty",
    };
  }
  const gaps = itemsWithGap(c.items, TAB_CRITERIA.kyvykkyydet);
  const value = `${c.total_items - gaps.length}/${c.total_items}`;
  if (gaps.length > 0) {
    return {
      ...base,
      value,
      hint: gapHint(gaps, TAB_CRITERIA.kyvykkyydet),
      tone: "warn",
    };
  }
  return { ...base, value, hint: "kaikki määritelty", tone: "ok" };
}

function signaalitDimension(c: BuildingCompleteness): ConfigDimension {
  const base = { key: "signaalit", label: TAB_LABEL.signaalit } as const;
  if (c.total_items === 0) {
    return {
      ...base,
      value: "—",
      hint: "Ei laitteita kytkettävänä",
      tone: "empty",
    };
  }
  const gaps = itemsWithGap(c.items, TAB_CRITERIA.signaalit);
  const value = `${c.total_items - gaps.length}/${c.total_items}`;
  if (gaps.length > 0) {
    return {
      ...base,
      value,
      hint: `${gaps.length} laitetta · ${gapHint(gaps, TAB_CRITERIA.signaalit)}`,
      tone: "warn",
    };
  }
  return { ...base, value, hint: "kaikki kytketty", tone: "ok" };
}

function palvelutDimension(services: BuildingService[]): ConfigDimension {
  const base = { key: "palvelut", label: TAB_LABEL.palvelut } as const;
  const total = services.length;
  const active = services.filter((s) => s.is_active);
  const value = total > 0 ? `${active.length}/${total}` : "0";
  if (total === 0) {
    return {
      ...base,
      value,
      hint: "Aktivoi sää- ja hintapalvelut",
      tone: "empty",
    };
  }
  if (active.length === 0) {
    return {
      ...base,
      value,
      hint: "Palvelut eivät ole käytössä",
      tone: "warn",
    };
  }
  const lastSync = active
    .map((s) => s.last_synced_at)
    .filter((t): t is string => t !== null)
    .sort()
    .at(-1);
  return {
    ...base,
    value,
    hint: lastSync ? `Synkronoitu ${formatRelativeFi(lastSync)}` : "aktiivista",
    tone: "ok",
  };
}

/** The four status cards, in tab-bar order. `completeness` is null when the endpoint failed. */
export function deriveDimensions(
  completeness: BuildingCompleteness | null,
  services: BuildingService[],
  dependencyCount = 0,
): ConfigDimension[] {
  // Order: Rakenne → Signaalit → Kyvykkyydet → Ulkoiset (design-system §2.4 / story #1140).
  return [
    completeness
      ? rakenneDimension(completeness, dependencyCount)
      : unavailableDimension("rakenne"),
    completeness
      ? signaalitDimension(completeness)
      : unavailableDimension("signaalit"),
    completeness
      ? kyvykkyydetDimension(completeness)
      : unavailableDimension("kyvykkyydet"),
    palvelutDimension(services),
  ];
}
